import { useEffect, useRef, useState } from 'react';
import { Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { Floating } from './ui/Floating';

// ─── Time helpers ─────────────────────────────────────────────────────────────
// All values stored as 24hr HH:MM, displayed using the standard 12hr format.

export function to12hr(time24: string): string {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${String(hour).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function to24hr(time12: string): string {
  if (!time12) return '';
  const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return time12;
  let h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  const ampm = match[3].toUpperCase();
  if (ampm === 'AM' && h === 12) h = 0;
  if (ampm === 'PM' && h !== 12) h += 12;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Decompose a 24hr "HH:MM" into the three modal columns.
function splitParts(time24: string): { hour: number; minute: number; ampm: 'AM' | 'PM' } {
  if (!time24) return { hour: 12, minute: 0, ampm: 'AM' };
  const [hRaw, mRaw] = time24.split(':').map(Number);
  const h24 = Number.isFinite(hRaw) ? hRaw : 0;
  const m = Number.isFinite(mRaw) ? mRaw : 0;
  const ampm: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
  const hour = h24 % 12 || 12;
  return { hour, minute: m, ampm };
}

function joinParts(hour: number, minute: number, ampm: 'AM' | 'PM'): string {
  let h24 = hour % 12;
  if (ampm === 'PM') h24 += 12;
  return `${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

// ─── Component ────────────────────────────────────────────────────────────────
//
// Renders a consistent 12-hour modal time picker across every browser/OS —
// matches the "Time (12 Hours)" modal screenshot shipped with the design.
// We can't rely on <input type="time"> here because Chrome on Windows shows
// a scrolling-wheel picker while Chrome on macOS shows the segmented modal.
// One custom component => one look everywhere.
//
// Props are unchanged from the legacy scrolling-list version: value is a
// 24hr "HH:MM" string, onChange receives the same. All existing call sites
// keep working (Time Availability, Lead Assign Working Hours, etc.).
// ────────────────────────────────────────────────────────────────────────────

interface TimeDropdownProps {
  /** Value in 24hr format: "09:00", "18:00" */
  value: string;
  /** Called with 24hr format value after the user presses OK. */
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function TimeDropdown({
  value,
  onChange,
  placeholder = 'Select time',
  className = '',
}: TimeDropdownProps) {
  const [open, setOpen] = useState(false);
  // Local draft state — only commits to `onChange` on OK so Cancel is a
  // real undo. Synced from `value` every time the modal opens.
  const initial = splitParts(value);
  const [hour, setHour] = useState<number>(initial.hour);
  const [minute, setMinute] = useState<number>(initial.minute);
  const [ampm, setAmpm] = useState<'AM' | 'PM'>(initial.ampm);

  const containerRef = useRef<HTMLDivElement>(null);

  // Reset the draft whenever the modal opens (or external value changes
  // while closed). Keeps Cancel honest — closing without OK never leaks
  // the user's mid-edit values back into the form.
  useEffect(() => {
    if (open) {
      const p = splitParts(value);
      setHour(p.hour);
      setMinute(p.minute);
      setAmpm(p.ampm);
    }
  }, [open, value]);

  // Outside-click + Escape are handled by <Floating> (below).

  const stepHour = (delta: number) => setHour((h) => {
    let next = h + delta;
    if (next < 1) next = 12;
    if (next > 12) next = 1;
    return next;
  });
  const stepMinute = (delta: number) => setMinute((m) => {
    // 5-minute granularity matches the legacy ALL_TIMES grid; tweak here
    // if a tenant ever needs minute-precision.
    let next = m + delta * 5;
    if (next < 0) next = 55;
    if (next > 59) next = 0;
    return next;
  });
  const toggleAmpm = () => setAmpm((a) => (a === 'AM' ? 'PM' : 'AM'));

  const handleOk = () => {
    onChange(joinParts(hour, minute, ampm));
    setOpen(false);
  };

  const displayed = value ? to12hr(value) : '';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors"
      >
        <span className={displayed ? 'text-slate-800' : 'text-slate-400'}>
          {displayed || placeholder}
        </span>
        <Clock className="h-3.5 w-3.5 text-slate-400 flex-shrink-0 ml-1" />
      </button>

      {/* Modal popover — portalled so it escapes overflow-hidden cards. */}
      <Floating
        open={open}
        anchorRef={containerRef}
        onClose={() => setOpen(false)}
        matchWidth={false}
        className="w-[280px] bg-white rounded-xl border border-slate-200 shadow-xl p-4"
      >
        <div role="dialog" aria-label="Time (12 Hours)">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-900">Time (12 Hours)</h4>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-600"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Three steppers — Hours / Minutes / AM-PM */}
          <div className="flex items-start justify-center gap-2 mb-4">
            <Stepper
              label="Hours"
              valueText={String(hour).padStart(2, '0')}
              onUp={() => stepHour(+1)}
              onDown={() => stepHour(-1)}
            />
            <div className="text-xl font-semibold text-slate-700 mt-7">:</div>
            <Stepper
              label="Minutes"
              valueText={String(minute).padStart(2, '0')}
              onUp={() => stepMinute(+1)}
              onDown={() => stepMinute(-1)}
            />
            <Stepper
              label="AM/PM"
              valueText={ampm}
              onUp={toggleAmpm}
              onDown={toggleAmpm}
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-1.5 rounded-md bg-slate-800 text-white text-sm font-medium hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleOk}
              className="px-4 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              OK
            </button>
          </div>
        </div>
      </Floating>
    </div>
  );
}

// ── Internal: stepper column with up/down chevrons and a value box ───────────

function Stepper({
  valueText,
  onUp,
  onDown,
  label,
}: {
  valueText: string;
  onUp: () => void;
  onDown: () => void;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1" aria-label={label}>
      <button
        type="button"
        onClick={onUp}
        className="text-blue-500 hover:text-blue-700 p-1"
        aria-label={`Increase ${label}`}
      >
        <ChevronUp className="h-4 w-4" />
      </button>
      <div className="min-w-[44px] h-8 rounded-md border border-slate-200 bg-white flex items-center justify-center text-base font-semibold text-slate-800">
        {valueText}
      </div>
      <button
        type="button"
        onClick={onDown}
        className="text-blue-500 hover:text-blue-700 p-1"
        aria-label={`Decrease ${label}`}
      >
        <ChevronDown className="h-4 w-4" />
      </button>
    </div>
  );
}
